import { Money, type DecimalString } from "@shop-ledger/shared-types";

/** One row of the manual-journal-entry form, before it's turned into a `LegIn`. Amounts
 * are kept as raw form-input strings (not yet `DecimalString`) since a partially-typed
 * field ("", "-", "12.") is a valid intermediate UI state that isn't a valid decimal yet. */
export interface JournalLegDraft {
  accountId: string;
  debit: string;
  credit: string;
}

export interface LedgerBalanceValidation {
  valid: boolean;
  errors: string[];
  totalDebit: DecimalString;
  totalCredit: DecimalString;
}

/**
 * Client-side pre-check for the manual journal entry form (Accountant Control Panel, §5),
 * mirroring the backend's own enforcement so a user gets immediate feedback instead of a
 * round trip to a 422/409:
 *
 *   - `backend/app/schemas/ledger.py::LedgerEntryCreate._must_balance` — **exact**
 *     equality between `sum(debit)` and `sum(credit)`, no rounding tolerance.
 *   - `ck_leg_non_negative` (DB CHECK, `backend/app/models/ledger.py`) — `debit >= 0 AND
 *     credit >= 0`.
 *   - `ck_leg_single_sided` (DB CHECK) — `NOT (debit > 0 AND credit > 0)`: a leg is
 *     debit-only or credit-only, never both nonzero at once.
 *
 * This is a UX convenience only, exactly like every other client-side check in this
 * monorepo — the DB's deferred constraint trigger (`trg_ledger_balance`, §6.5) remains the
 * actual enforced invariant regardless of what this function decides. All arithmetic goes
 * through `Money` (decimal.js-backed) per `shared-types`' "never call `Number()` on a
 * money field" rule — float addition on strings like "0.1" + "0.2" would produce exactly
 * the kind of off-by-a-cent false "unbalanced" rejection this function exists to avoid.
 */
export function validateJournalLegs(legs: readonly JournalLegDraft[]): LedgerBalanceValidation {
  const errors: string[] = [];
  const debitStrings: string[] = [];
  const creditStrings: string[] = [];

  if (legs.length < 2) {
    errors.push("A journal entry needs at least two legs.");
  }

  legs.forEach((leg, index) => {
    const rowLabel = `Row ${index + 1}`;
    if (!leg.accountId) {
      errors.push(`${rowLabel}: select an account.`);
    }

    let debit;
    let credit;
    try {
      debit = Money.of(leg.debit.trim() === "" ? "0" : leg.debit);
    } catch {
      errors.push(`${rowLabel}: debit is not a valid amount.`);
      return;
    }
    try {
      credit = Money.of(leg.credit.trim() === "" ? "0" : leg.credit);
    } catch {
      errors.push(`${rowLabel}: credit is not a valid amount.`);
      return;
    }

    if (debit.isNegative() || credit.isNegative()) {
      errors.push(`${rowLabel}: amounts cannot be negative.`);
    }
    if (debit.greaterThan(0) && credit.greaterThan(0)) {
      errors.push(`${rowLabel}: a leg must be debit-only or credit-only, not both.`);
    }
    if (debit.isZero() && credit.isZero()) {
      errors.push(`${rowLabel}: enter a debit or credit amount.`);
    }

    debitStrings.push(leg.debit.trim() === "" ? "0" : leg.debit);
    creditStrings.push(leg.credit.trim() === "" ? "0" : leg.credit);
  });

  const totalDebit = Money.sum(debitStrings);
  const totalCredit = Money.sum(creditStrings);

  if (errors.length === 0 && legs.length >= 2 && Money.compare(totalDebit, totalCredit) !== 0) {
    errors.push(`Entry does not balance: total debits ${totalDebit} ≠ total credits ${totalCredit}.`);
  }

  return { valid: errors.length === 0, errors, totalDebit, totalCredit };
}
